import React from "react";
import { useForm } from "react-hook-form";
import TextInput from "../TextInput/TextInput";
import { Wrapper, Card, Title, Form } from "./RecommendationStep.elements";
import Checkbox, { CheckboxLink } from "../Checkbox/Checkbox";
import { Trans, useTranslation } from "react-i18next";
import { ButtonPrimary } from "../Button/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  DispatchType,
  registrationActions,
  registrationSelectors,
  showModal,
} from "../../redux";
import ErrorModalContent from "../ErrorModalContent";
import { Spinner } from "../Spinner/Spinner";

export type RecommendationStepProps = {
  referralCode: string;
  setReferralCode: (referralCode: string) => void;
  joined: boolean;
  setJoined: (joined: boolean) => void;
  onFinish: (data: any) => void;
  email: string;
  companyName: string;
  regNumber: string;
  taxNumber: string;
  confirmationCode: string;
};

export default function RecommendationStep({
  referralCode,
  setReferralCode,
  joined,
  setJoined,
  onFinish,
  email,
  companyName,
  regNumber,
  taxNumber,
  confirmationCode,
}: RecommendationStepProps) {
  const { t, i18n } = useTranslation();
  const { register, clearErrors } = useForm({ mode: "onSubmit" });
  const dispatch = useDispatch<DispatchType>();
  const userCreationLoading = useSelector(
    registrationSelectors.getUserCreationLoading
  );

  async function handleSubmit() {
    try {
      await dispatch(
        registrationActions.createLegalUser({
          email: email,
          registrationCode: confirmationCode,
          fullName: companyName,
          registrationNumber: regNumber,
          taxIdNumber: taxNumber,
          referrerCode: referralCode,
          joinReferralProgram: joined,
        })
      )
        .unwrap()
        .then(() => {
          onFinish({ referralCode, joined });
        })
        .catch((error) => {
          dispatch(
            showModal({
              title: t("common.error.title"),
              content: <ErrorModalContent message={error} />,
            })
          );
        });
    } catch (error) {
      console.error("User creation failed:", error);
    }
  }

  const lang = i18n.language as "hu" | "en" | "lv";
  const links = {
    hu: {
      pp: "https://data.blockben.com/gdpr/Privacy_Policy_hu.pdf",
      toc: "https://data.blockben.com/terms/A_BlockWallet_GTC_hu.pdf",
    },
    en: {
      pp: "https://data.blockben.com/gdpr/Privacy_Policy_en.pdf",
      toc: "https://data.blockben.com/terms/A_BlockWallet_GTC_en.pdf",
    },
    lv: {
      pp: "https://data.blockben.com/gdpr/Privacy_Policy_lv.pdf",
      toc: "https://data.blockben.com/terms/A_BlockWallet_GTC_lv.pdf",
    },
  };
  const currentLinks = links[lang];

  return (
    <Wrapper>
      <Card>
        <Title>
          <Trans i18nKey="registration.recommendationStep.title" />
        </Title>
        <Form>
          <TextInput
            label={t("registration.recommendationStep.referralCodeLabel")}
            name="referralCode"
            value={referralCode}
            onChange={(value: string) => {
              setReferralCode(value);
              clearErrors("referralCode");
            }}
            register={register}
            placeholder={t(
              "registration.recommendationStep.referralCodePlaceholder"
            )}
          />
          <Checkbox
            id="joined"
            checked={!!joined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setJoined(e.target.checked);
              clearErrors("joined");
            }}
            label={
              <Trans
                i18nKey="registration.recommendationStep.checkboxLabel"
                components={{
                  pp: <CheckboxLink href={currentLinks.pp} target="_blank" />,
                  toc: <CheckboxLink href={currentLinks.toc} target="_blank" />,
                }}
              />
            }
          />
          <ButtonPrimary
            disabled={userCreationLoading}
            style={{
              width: "fit-content",
              marginLeft: "auto",
              marginRight: "auto",
            }}
            onClick={handleSubmit}
          >
            {userCreationLoading && <Spinner size="small" />}
            {<Trans i18nKey="registration.recommendationStep.submit" />}
          </ButtonPrimary>
        </Form>
      </Card>
    </Wrapper>
  );
}
